import axios from 'axios';
import { addMinutes, getMinutes } from 'date-fns';
import jwtDecode from 'jwt-decode';

import AsyncStorage from '@react-native-community/async-storage';
import refreshToken from '@services/api/refreshToken';

import { reset } from '../../routes/rootNavigation';

interface TokenInterface {
  exp: number;
}

const api = axios.create({
  baseURL: process.env.EXPO_APP_API_URL
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  async response => {
    return response;
  },
  async error => {
    if (!error.config.url?.includes('auth/callback')) {
      const originalRequest = error.config;

      if (error.response.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return api(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { data } = await refreshToken();
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          processQueue(null, data.token);
          return api(originalRequest);
        } catch (err) {
          processQueue(err, null);
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      if (error.response.status === 403 || error.response.status === 500) {
        reset({
          index: 0,
          routes: [{ name: 'Robot' }]
        });
      }
    }

    return Promise.reject(error);
  }
);

api.interceptors.request.use(
  async config => {
    if (!config.url?.includes('auth/callback')) {
      let expired = true;
      let tokenData = null;
      const token = await AsyncStorage.getItem('@cartaoluiza:token');

      try {
        tokenData = jwtDecode<TokenInterface>(token as string);
        const minutes = getMinutes(tokenData.exp);
        const newExp = addMinutes(tokenData.exp, minutes);

        expired = newExp.getTime() < Date.now() / 1000;
      } catch (error) {}

      if (!expired) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // eslint-disable-next-line no-param-reassign
    config.headers.Accept = 'application/vnd.cartaoluiza.v1+json';

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

export default api;
